import { useEffect, useState } from "react";
import { FaTrash, FaPlus } from "react-icons/fa";
import { PROJECTS_CONSTANTS } from "../_MODULES/_API";
import { OPTIONS_PROJECT_STATUS, OPTIONS_ROUTE_ENVIRONMENT, PALETTE_HEX } from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FormGrid, FormSection, FieldInput, FieldTextarea, FieldSelect, FieldColor, FieldTags, FieldDate, FieldSwitch, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";

const EMPTY_ROUTE = { label: "", url: "", environment: "LOCAL" };

const REGEX_URL = /^https?:\/\/[^\s]+\.[^\s]{2,}$|^https?:\/\/localhost(:\d+)?(\/.*)?$/i;

const onNormalizeUrl = (url) => url.trim().toLowerCase().replace(/\/+$/, "");

const onValidateRoutes = (routes) => {

    const ERRORS = {};
    const SEEN = new Map();

    routes.forEach((route, index) => {

        const URL = route.url.trim();

        if (!URL) {
            if (route.label.trim()) ERRORS[index] = "Escribí la dirección o borrá la fila";
            return;
        }

        if (!REGEX_URL.test(URL)) {
            ERRORS[index] = "Tiene que empezar con http:// o https:// y tener un dominio válido";
            return;
        }

        const KEY = onNormalizeUrl(URL);

        if (SEEN.has(KEY)) {
            ERRORS[index] = `Ruta repetida, ya está en la fila ${SEEN.get(KEY) + 1}`;
            return;
        }

        SEEN.set(KEY, index);
    });

    return ERRORS;
};

const EMPTY_VALUES = {
    companyId: "", name: "", description: "", accessPin: "",
    repositoryFrontend: "", repositoryBackend: "", repositoryOther: "",
    status: "ACTIVE", color: "#e83a52", stack: [], startedAt: null, isFavorite: false,
};

export const ProjectFormModal = ({ isOpen, project = null, projects = [], companies = [], defaultCompanyId = null, isCompanyLocked = false, onClose, onSaved }) => {

    const IS_EDIT = !!project?.id;

    const [useRoutes, setRoutes] = useState([{ ...EMPTY_ROUTE }, { ...EMPTY_ROUTE }, { ...EMPTY_ROUTE }]);
    const [useRouteErrors, setRouteErrors] = useState({});
    const [useTemplates, setTemplates] = useState([]);
    const [useTemplateId, setTemplateId] = useState("");

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 400,
    });

    const onChangeRoute = (index, field, value) => {

        setRouteErrors((prev) => ({ ...prev, [index]: null }));

        setRoutes((prev) => prev.map((route, position) => (
            position === index ? { ...route, [field]: value } : route
        )));
    };

    const onAddRoute = () => setRoutes((prev) => [...prev, { ...EMPTY_ROUTE }]);

    const onRemoveRoute = (index) => {
        setRouteErrors({});
        setRoutes((prev) => prev.filter((route, position) => position !== index));
    };

    const FORM = useForm({
        initialValues: EMPTY_VALUES,

        schema: {
            companyId: { required: true },
            name: { required: true, minLength: 2 },
        },

        onSubmit: async (values) => {

            if (isDuplicateRecord({ rows: projects, value: values.name, currentId: project?.id, scope: values.companyId || "", getScope: (row) => row?.companyId || row?.company?.id || "" })) {
                FORM.setError("name", "Ya existe un proyecto con ese nombre en esta compañía", { focus: true });
                return;
            }

            const ROUTE_ERRORS = onValidateRoutes(useRoutes);

            if (Object.values(ROUTE_ERRORS).some((error) => !!error)) {
                setRouteErrors(ROUTE_ERRORS);
                return;
            }

            const ROUTES = useRoutes
                .filter((route) => !!route.url.trim())
                .map((route, index) => ({
                    label: route.label || undefined,
                    url: route.url.trim(),
                    environment: route.environment,
                    position: index,
                }));

            const PAYLOAD = {
                companyId: values.companyId,
                name: values.name.trim(),
                description: values.description || undefined,
                accessPin: values.accessPin || undefined,
                repositoryFrontend: values.repositoryFrontend || undefined,
                repositoryBackend: values.repositoryBackend || undefined,
                repositoryOther: values.repositoryOther || undefined,
                status: values.status,
                color: values.color || undefined,
                stack: values.stack?.length > 0 ? values.stack : undefined,
                startedAt: values.startedAt ? new Date(values.startedAt).toISOString() : undefined,
                isFavorite: !!values.isFavorite,
                routes: ROUTES,
            };

            const RESPONSE = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: PROJECTS_CONSTANTS.UPDATE, successMessage: "Proyecto actualizado", id: project.id, data: PAYLOAD })
                : await actionMutate({ method: "POST", endpoint: PROJECTS_CONSTANTS.CREATE, successMessage: "Proyecto creado", data: PAYLOAD });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            if (!IS_EDIT && useTemplateId && RESPONSE.data?.id) {
                await API_CORE.SERVICES.POST_RESOURCE(`/control/templates/${useTemplateId}/apply/${RESPONSE.data.id}`, {});
            }

            onSaved?.(RESPONSE.data, IS_EDIT);
            onClose?.();
        },
    });

    useEffect(() => {

        if (!isOpen) return;

        if (IS_EDIT) {

            FORM.setValues({
                companyId: project.companyId ?? project.company?.id ?? "",
                name: project.name ?? "",
                description: project.description ?? "",
                accessPin: "",
                repositoryFrontend: project.repositoryFrontend ?? "",
                repositoryBackend: project.repositoryBackend ?? "",
                repositoryOther: project.repositoryOther ?? "",
                status: project.status ?? "ACTIVE",
                color: project.color ?? "#e83a52",
                stack: project.stack ?? [],
                startedAt: project.startedAt ? new Date(project.startedAt) : null,
                isFavorite: !!project.isFavorite,
            });

            const SAVED = [...(project.routes ?? [])]
                .sort((a, b) => a.position - b.position)
                .map((route) => ({ label: route.label ?? "", url: route.url, environment: route.environment }));

            setRoutes(SAVED.length > 0 ? SAVED : [{ ...EMPTY_ROUTE }]);
            setRouteErrors({});

            return;
        }

        FORM.setValues({ ...EMPTY_VALUES, companyId: defaultCompanyId || companies[0]?.id || "" });
        setRoutes([{ ...EMPTY_ROUTE }, { ...EMPTY_ROUTE }, { ...EMPTY_ROUTE }]);
        setRouteErrors({});
        setTemplateId("");
        API_CORE.SERVICES.GET_RESOURCES("/control/templates", {}).then((response) => {
            const rows = response?.data?.data ?? response?.data ?? response;
            setTemplates(Array.isArray(rows) ? rows : []);
        });

    }, [isOpen, project]); // eslint-disable-line react-hooks/exhaustive-deps

    const COMPANY_OPTIONS = companies.map((company) => ({ value: company.id, label: company.name }));

    return (
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
                isDirty={FORM.isDirty}
            size="xl"
            title={IS_EDIT ? "Editar proyecto" : "Nuevo proyecto"}
            description="Los subproyectos de frontend y backend se crean después, dentro del proyecto."
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>
                        {IS_EDIT ? "Guardar cambios" : "Crear proyecto"}
                    </AppButton>
                </>
            } >

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="project_isFavorite" label="Destacado" text="Mostrar este proyecto primero"
                    isChecked={!!FORM.values.isFavorite}
                    onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

            {!IS_EDIT && (
                <FormSection title="Plantilla inicial" description="Opcional: crea Trello, Pasos, Guías y Runbooks base después de crear el proyecto.">
                    <FieldSelect id="project_template" label="Usar plantilla" value={useTemplateId}
                        onChange={(event) => setTemplateId(event.target.value)}
                        options={useTemplates.map((item) => ({ value: item.id, label: item.name }))}
                        placeholder="Proyecto vacío" />
                </FormSection>
            )}

            <FormSection title="Datos generales">

                <FormGrid columns={3}>

                    <FieldSelect {...FORM.field("companyId")} label="Compañía" options={COMPANY_OPTIONS}
                        placeholder="Elegí una compañía" isDisabled={isCompanyLocked} isSearchable />

                    <FieldInput {...FORM.field("name")} label="Nombre" placeholder="Ej: Portal de Operaciones" />

                    <FieldInput {...FORM.field("accessPin")} type="pin" label="Pin de acceso"
                        placeholder={IS_EDIT ? "Dejalo vacío para no cambiarlo" : "4 a 12 dígitos"}
                        hint={IS_EDIT && project.hasAccessPin ? "Este proyecto ya tiene pin" : "Opcional"} />

                    <FieldTextarea {...FORM.field("description")} label="Descripción" rows={3} span={3} placeholder="Ej: Portal interno de operaciones" />

                    <FieldSelect {...FORM.field("status")} label="Estado" options={OPTIONS_PROJECT_STATUS} isSearchable />

                    <FieldColor {...FORM.field("color")} label="Color" swatches={PALETTE_HEX} />

                    <FieldDate {...FORM.fieldValue("startedAt", { emptyValue: null })} label="Fecha de inicio" />


                    <FieldTags {...FORM.fieldValue("stack", { emptyValue: [] })} label="Stack"
                        placeholder="Ej: nest, react, postgres" span={3} />

                </FormGrid>

            </FormSection>

            <FormSection title="Repositorios">

                <FormGrid columns={3}>

                    <FieldInput {...FORM.field("repositoryFrontend")} label="Frontend" placeholder="Ej: https://github.com/usuario/repo" />

                    <FieldInput {...FORM.field("repositoryBackend")} label="Backend" placeholder="Ej: https://github.com/usuario/repo" />

                    <FieldInput {...FORM.field("repositoryOther")} label="Otro repositorio o archivo"
                        placeholder="Ej: https://drive.google.com/carpeta" />

                </FormGrid>

            </FormSection>

            <FormSection title="Rutas" description="La dirección donde corre el proyecto en cada entorno." columns={1}>

                <div className="LStudio__app_project_routes">

                    <div className="LStudio__app_project_routes_header">
                        <span>Nombre</span>
                        <span>Dirección</span>
                        <span>Entorno</span>
                        <span />
                    </div>

                    {
                        useRoutes.map((route, index) => (
                            <div key={index} className="LStudio__app_project_routes_row"
                                data-invalid={!!useRouteErrors[index]}>

                                <FieldInput id={`route_label_${index}`} placeholder="Ej: App" value={route.label}
                                    onChange={(event) => onChangeRoute(index, "label", event.target.value)} />

                                <FieldInput id={`route_url_${index}`} placeholder="Ej: https://app.example.com"
                                    value={route.url} error={useRouteErrors[index] ?? null}
                                    onChange={(event) => onChangeRoute(index, "url", event.target.value)} />

                                <FieldSelect id={`route_env_${index}`} options={OPTIONS_ROUTE_ENVIRONMENT} isSearchable
                                    value={route.environment}
                                    onChange={(event) => onChangeRoute(index, "environment", event.target.value)} />

                                <AppButton size="sm" variant="ghost" tone="danger" icon={<FaTrash />} isIconOnly
                                    aria-label="Quitar ruta" isDisabled={useRoutes.length === 1}
                                    onClick={() => onRemoveRoute(index)} />

                            </div>
                        ))
                    }

                </div>

                <div className="LStudio__app_project_routes_footer">
                    <AppButton size="sm" variant="soft" tone="neutral" icon={<FaPlus />} onClick={onAddRoute}>
                        Agregar ruta
                    </AppButton>
                </div>

            </FormSection>

        </AppModal>
    );
};
